import { faCircleNotch } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useCallback, useEffect, useState } from "react";
import { Alert, Button, Card } from "react-bootstrap";
import { defineMessages, FormattedMessage } from "react-intl";
import styled from "styled-components";
import { withAuthorizationVerifier } from "../../components/authorization-verifier";
import { SectionHeading } from "../../components/section-heading";
import Selector from "../../components/selector";
import { useUserPropertyTickers } from "../../components/user-tickers-providers";
import { ACCESS_DENIED } from "../../lib/auth";
import { useConfig } from "../../lib/config";
import { useHttpClient } from "../../lib/http";
import { regenPropertyCache } from "../common/api";
import { defaultFormMessages } from "../shared/crud/form";
import { defaultListMessages } from "../shared/crud/list";
import { regenPropertyCalendar, regenPropertyInventoryCalendar } from "./api";

const LoaderWrapper = styled.div`
  width: 100%;
  height: 100%;
  font-size: 50px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  line-height: 100%;
  color: #cccccc;
  opacity: 0.6;
  background: white;
  z-index: 1;
`;

export function Loader({ children }: { children: React.ReactNode }) {
  return (
    <LoaderWrapper>
      <FontAwesomeIcon icon={faCircleNotch} spin />
      {children}
    </LoaderWrapper>
  );
}

const messages = {
  ...defaultFormMessages,
  ...defaultListMessages,
  ...defineMessages({
    title: {
      id: "internalmenu.utilities.propertyCalendarRegen",
      defaultMessage: "Regen property calendar",
    },
    propertyTickerSelectPlaceholder: {
      id: "channelManager.placeholder.propertyTicker",
      defaultMessage: "Engine identifier",
    },
  }),
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  margin: 1rem;
  > * {
    margin: 1rem 0;
  }
`;
const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin: 1rem;
`;
const Label = styled.div`
  font-size: 18px;
  margin-bottom: -10px;
`;

type Option = { value: string; label: string };

interface OperationError {
  message: string;
}

function PropertyCalendarRegen() {
  const userPropertyTickersList = useUserPropertyTickers();
  const config = useConfig();
  const httpClient = useHttpClient(config.secureApiEndpoint);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<OperationError | null | undefined>(undefined);
  const [operations, setOperations] = useState<string[]>([
    "regenPropertyCache",
    "regenPropertyInventoryCalendar",
    "regenPropertyCalendar",
  ]);
  const [configSuccess, setConfigSuccess] = useState<boolean>(false);

  const [propertyTickerOptions, setPropertyTickerOptions] = useState<Option[]>([]);
  const [propertyTickerOption, setPropertyTickerOption] = useState<Option | null | undefined>(
    undefined
  );

  useEffect(() => {
    setPropertyTickerOptions(
      userPropertyTickersList.map(option => ({ value: option, label: option }))
    );
  }, [userPropertyTickersList]);

  const handleSelectPropertyTicker = useCallback(
    (selectedOption: Option | null | undefined) => setPropertyTickerOption(selectedOption),
    [setPropertyTickerOption]
  );

  const regenCalendar = useCallback(async () => {
    if (!propertyTickerOption || !propertyTickerOption.value) {
      return;
    }
    setLoading(true);
    setError(undefined);
    setConfigSuccess(false);
    setOperations([]);
    try {
      await regenPropertyCache(httpClient, propertyTickerOption.value, true);
      console.log("regenPropertyCache DONE");
      setOperations(["regenPropertyCache"]);
      await regenPropertyInventoryCalendar(httpClient, propertyTickerOption.value);
      console.log("regenPropertyInventoryCalendar DONE");
      setOperations(["regenPropertyCache", "regenPropertyInventoryCalendar"]);
      await regenPropertyCalendar(httpClient, propertyTickerOption.value);
      console.log("regenPropertyCalendar DONE");
      setOperations([
        "regenPropertyCache",
        "regenPropertyInventoryCalendar",
        "regenPropertyCalendar",
      ]);
      setConfigSuccess(true);
    } catch (e) {
      setConfigSuccess(false);
      if (e === ACCESS_DENIED) {
        console.error("Access denied", e);
        setError({ message: "Access denied" });
      } else {
        console.error("Error retrieving children", e);
        setError({ message: "Error creating chain" });
      }
    }
    setLoading(false);
  }, [httpClient, propertyTickerOption]);

  return (
    <>
      <SectionHeading>
        <FormattedMessage {...messages.title} />
      </SectionHeading>
      {loading ? (
        <Loader>
          {operations.map(it => (
            <div style={{ fontSize: 14, color: "black" }}>OK: {it}</div>
          ))}
        </Loader>
      ) : null}
      <Card className="ml-4 mr-4 mb-4 mt-4">
        <Container>
          <Label>
            <FormattedMessage {...messages.propertyTickerSelectPlaceholder} />
          </Label>
          <Selector
            options={propertyTickerOptions}
            value={propertyTickerOption}
            onChange={handleSelectPropertyTicker}
            placeholder={<FormattedMessage {...messages.propertyTickerSelectPlaceholder} />}
          />
        </Container>
        {error && (
          <Alert variant="danger">
            <strong>
              <FormattedMessage {...messages.saveError} />
            </strong>
          </Alert>
        )}
        {configSuccess && (
          <Alert variant="success">
            <strong>
              <FormattedMessage {...messages.saveSuccess} />
            </strong>
          </Alert>
        )}
        <ButtonContainer>
          <Button variant="primary" type="button" onClick={regenCalendar}>
            <FormattedMessage {...messages.save} />
          </Button>
        </ButtonContainer>
      </Card>
    </>
  );
}

export default withAuthorizationVerifier(PropertyCalendarRegen);
